import { getKnowledgeBaseDownloadLabelKey } from "./knowledgeBaseDownload"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ChevronRight,
  ChevronDown,
  FolderOpen,
  Folder,
  FileImage,
  FileText,
  Search,
  X,
  FolderPlus,
  FilePlus2,
  Trash2,
  MoreHorizontal,
  Upload,
  Loader2,
  XCircle,
  CirclePlus,
  Pencil,
  Check,
  ListCollapse,
  Download,
  Globe,
  Settings,
  FolderUp,
  Info,
  ArrowRight,
  TriangleAlert,
  BriefcaseBusiness,
  ExternalLink,
} from "lucide-react"
import type { UseKnowledgeBaseReturn } from "./hooks/useKnowledgeBase"
import { useWebsiteCrawlerDialog } from "./hooks/useWebsiteCrawlerDialog"
import type { KnowledgeNode } from "./types"
import { WebsiteCrawlerDialog } from "./WebsiteCrawlerDialog"
import { WebsiteCrawlSummary } from "./WebsiteCrawlSummary"
import { cn } from "@f2e/ui/lib/utils"
import { Input } from "@f2e/ui/components/base"
import { Button } from "@f2e/ui/components/base"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@f2e/ui/components/base"
import { Label } from "@f2e/ui/components/base"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@f2e/ui/components/base"
import {
  SUPPORTED_MIME_TYPES,
  buildFileAcceptString,
} from "@f2e/shared/constants"
import { useContentSelection } from "./hooks/useContentSelection"
import { useKnowledgeBaseDropUpload } from "./hooks/useKnowledgeBaseDropUpload"
import { KnowledgeBaseTextRangeFaqViewer } from "./KnowledgeBaseTextRangeFaqViewer"
import { KnowledgeBaseImagePreview } from "./KnowledgeBaseImagePreview"
import {
  getImageFormatLabel,
  getImageSearchDescription,
  isImageNode,
} from "./knowledgeBaseImage"
import { TabularFileDetail } from "./TabularFileDetail"
import { isPotentialTabularKnowledgeNode } from "./tabular"

import { ContextProvider, Permission } from "@/types"
import { ProviderLogo } from "@/components/ProviderLogo"
import { useToast } from "@/hooks"
import { useStore } from "@/store"
import { escapeTildeCodeFencesForPreview } from "./markdownPreview"
import { useKnowledgeBaseTabularCapabilitiesApi } from "@/hooks/api"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"
import { hasPermission } from "@/lib/utils"

const KB_FILE_ACCEPT_STRING = buildFileAcceptString(SUPPORTED_MIME_TYPES)
const KB_SUPPORTED_FORMATS_DOCS_PATH =
  "/connect-data/knowledge-base/#supported-sources-and-file-types"

export const getKnowledgeBaseSupportedFormatsDocsUrl = (language: string) => {
  const localePrefix =
    language === "zh-TW" || language === "ja" ? `/${language}` : ""
  return `https://docs.codeer.ai${localePrefix}${KB_SUPPORTED_FORMATS_DOCS_PATH}`
}

const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B"
  const sizes = ["B", "KB", "MB", "GB", "TB"]
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`
}

const EMPTY_CHILDREN: KnowledgeNode[] = []

const getNodeStatusKey = (node: KnowledgeNode) => {
  if (node.node_type === "FOLDER") {
    if (node.source_type === "WEBSITE_CRAWLER") return "websiteCrawler"
    return "folder"
  }

  switch (node.status) {
    case "READY":
      return "ready"
    case "PENDING":
      return "pending"
    case "INDEXING":
      return "indexing"
    case "FAILED":
      return "failed"
    default:
      return "unknown"
  }
}

interface TreeNodeState {
  // Map of folder-node id -> expanded state (true = expanded).
  [nodeId: string]: boolean
}

const AddFileMenuItems = ({
  folderId,
  connections,
  isStorageAtLimit,
  onUpload,
  onAddFromGoogleDriveConnection,
  onAddFromNotionConnection,
  onConnectAnotherGoogleAccount,
  onConnectAnotherNotionWorkspace,
  onDeleteConnection,
}: {
  folderId: string
  connections: Array<{
    id: string
    name: string
    status: string
    provider: string
  }>
  isStorageAtLimit: boolean
  onUpload: (folderId: string) => void
  onAddFromGoogleDriveConnection: (
    folderId: string,
    connectionId: string
  ) => void
  onAddFromNotionConnection: (folderId: string, connectionId: string) => void
  onConnectAnotherGoogleAccount: (folderId: string) => void
  onConnectAnotherNotionWorkspace: (folderId: string) => void
  onDeleteConnection: (connectionId: string) => void
}) => {
  const { t, i18n } = useTranslation()
  const supportedFormatsDocsUrl = getKnowledgeBaseSupportedFormatsDocsUrl(
    i18n.resolvedLanguage || i18n.language
  )

  // Show both connected + unconnected connections. We keep "connect another ..." actions hidden
  // as long as there is at least one connection of that provider (even if it's currently unconnected),
  // so users are guided to re-auth via the existing connection instead of creating duplicates.
  const googleDriveConnections = connections.filter(
    (c) => c.provider === ContextProvider.GoogleDrive
  )
  const notionConnections = connections.filter(
    (c) => c.provider === ContextProvider.Notion
  )
  const hasAnyGoogleDriveConnection = googleDriveConnections.length > 0
  const hasAnyNotionConnection = notionConnections.length > 0

  return (
    <>
      {isStorageAtLimit && (
        <>
          <DropdownMenuLabel className="text-xs text-red-600 dark:text-red-400">
            {t("knowledgeBase.treeView.storageLimitReached")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
        </>
      )}

      <DropdownMenuItem
        className="gap-2"
        disabled={isStorageAtLimit}
        onClick={(e) => {
          e.stopPropagation()
          onUpload(folderId)
        }}
      >
        <Upload className="w-4 h-4" />
        {t("knowledgeBase.treeView.uploadFile")}
      </DropdownMenuItem>

      <DropdownMenuLabel className="max-w-72 whitespace-normal px-2 pb-1 text-xs font-normal leading-relaxed text-muted-foreground">
        {t("knowledgeBase.treeView.uploadHelp")}
      </DropdownMenuLabel>

      <DropdownMenuItem asChild className="text-xs">
        <a
          href={supportedFormatsDocsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {t("knowledgeBase.treeView.supportedFormatsLink")}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuLabel className="text-xs text-slate-500 dark:text-slate-400">
        {t("knowledgeBase.treeView.addFromConnection")}
      </DropdownMenuLabel>

      {hasAnyGoogleDriveConnection && (
        <DropdownMenuLabel className="max-w-72 whitespace-normal px-2 pb-2 text-xs font-normal leading-relaxed text-muted-foreground">
          {t("knowledgeBase.treeView.googleDriveHelp")}
        </DropdownMenuLabel>
      )}

      {connections.map((c) => {
        const provider = c.provider || "UNKNOWN"
        const isGoogleDrive = provider === ContextProvider.GoogleDrive
        const isNotion = provider === ContextProvider.Notion
        return (
          <DropdownMenuItem
            key={c.id}
            className="gap-2 group"
            disabled={isStorageAtLimit || (!isGoogleDrive && !isNotion)}
            onClick={(e) => {
              e.stopPropagation()
              if (isGoogleDrive) {
                onAddFromGoogleDriveConnection(folderId, c.id)
                return
              }
              if (isNotion) {
                onAddFromNotionConnection(folderId, c.id)
                return
              }
            }}
          >
            <ProviderLogo
              provider={provider as ContextProvider}
              className="w-4 h-4"
            />
            <span className="flex-1">{c.name}</span>
            {(isGoogleDrive || isNotion) && (
              <button
                className="ml-auto p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation()
                  onDeleteConnection(c.id)
                }}
                aria-label={t("knowledgeBase.treeView.deleteConnection")}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </DropdownMenuItem>
        )
      })}

      {!hasAnyGoogleDriveConnection && (
        <DropdownMenuItem
          className="gap-2"
          disabled={isStorageAtLimit}
          onClick={(e) => {
            e.stopPropagation()
            onConnectAnotherGoogleAccount(folderId)
          }}
        >
          <CirclePlus className="w-4 h-4" />
          {t("knowledgeBase.treeView.connectAnotherGoogleAccount")}
        </DropdownMenuItem>
      )}

      {!hasAnyNotionConnection && (
        <DropdownMenuItem
          className="gap-2"
          disabled={isStorageAtLimit}
          onClick={(e) => {
            e.stopPropagation()
            onConnectAnotherNotionWorkspace(folderId)
          }}
        >
          <CirclePlus className="w-4 h-4" />
          {t("knowledgeBase.treeView.connectAnotherNotionWorkspace")}
        </DropdownMenuItem>
      )}
    </>
  )
}

const renderNodeIcon = (
  node: KnowledgeNode,
  opts?: {
    isExpanded?: boolean
    className?: string
    wrapperClassName?: string
    isWebsiteCrawlSyncing?: boolean
  }
) => {
  const isExpanded = opts?.isExpanded ?? false
  const isWebsiteCrawlSyncing = opts?.isWebsiteCrawlSyncing ?? false
  const iconClassName = cn("w-4 h-4 flex-shrink-0", opts?.className)
  const wrapperClassName = cn(
    "w-4 h-4 flex-shrink-0 flex items-center justify-center",
    opts?.wrapperClassName
  )

  if (node.ui_kind === "WORKSPACE") {
    return (
      <BriefcaseBusiness
        className={cn(iconClassName, "text-slate-500 dark:text-slate-400")}
      />
    )
  }

  if (node.node_type === "FOLDER") {
    if (node.source_type === "WEBSITE_CRAWLER") {
      if (isWebsiteCrawlSyncing) {
        return (
          <div className={wrapperClassName}>
            <Loader2
              className={cn(
                iconClassName,
                "animate-spin text-cyan-600 dark:text-cyan-300"
              )}
            />
          </div>
        )
      }
      return (
        <Globe
          className={cn(iconClassName, "text-cyan-600 dark:text-cyan-300")}
        />
      )
    }
    return isExpanded ? (
      <FolderOpen className={cn(iconClassName, "text-amber-500")} />
    ) : (
      <Folder className={cn(iconClassName, "text-amber-500")} />
    )
  }

  const isIndexing = node.status === "PENDING" || node.status === "INDEXING"
  const isFailed = node.status === "FAILED"

  return (
    <div className={wrapperClassName}>
      {isIndexing ? (
        <Loader2 className={cn(iconClassName, "animate-spin text-slate-500")} />
      ) : isFailed ? (
        <XCircle className={cn(iconClassName, "text-red-500")} />
      ) : isImageNode(node) ? (
        <FileImage className={cn(iconClassName, "text-blue-500")} />
      ) : (
        <FileText className={cn(iconClassName, "text-blue-500")} />
      )}
    </div>
  )
}

const ACTIVE_WEBSITE_CRAWL_JOB_STATUSES = new Set(["PENDING", "RUNNING"])

/**
 * TreeView - A collapsible tree/outline view for knowledge base
 * Features:
 * - Hierarchical tree structure with expand/collapse
 * - Search/filter functionality
 * - Inline actions (create folder, upload, add from connection, delete)
 * - Selection with detail preview
 */
const TreeView = ({ kb }: { kb: UseKnowledgeBaseReturn }) => {
  const { t } = useTranslation()
  const translationRef = useRef(t)
  useEffect(() => {
    translationRef.current = t
  }, [t])
  const { alertToast } = useToast()
  const [userMe, currentOrganization, storeWorkspaces] = useStore((s) => [
    s.userMe,
    s.currentOrganization,
    s.workspaces,
  ])
  const { fetchFn: fetchTabularCapabilities } =
    useKnowledgeBaseTabularCapabilitiesApi()

  // Filter connections to only show current user's connections
  const myConnections = useMemo(() => {
    if (!userMe) return []
    return kb.connections.filter(
      (conn) => conn.ownerId === userMe.id.toString()
    )
  }, [kb.connections, userMe])

  // Current selection in the explorer tree (owned by `useKnowledgeBase`).
  const selectedNode = kb.selectedNode
  // Controller method used to fetch and cache file content into the selected node.
  const fetchFileContent = kb.fetchFileContent
  // Only file nodes have content rendered in the detail panel.
  const selectedContent =
    selectedNode?.node_type === "FILE" ? (selectedNode.content ?? null) : null
  const selectedPreviewContent = useMemo(
    () =>
      selectedContent === null
        ? null
        : escapeTildeCodeFencesForPreview(selectedContent),
    [selectedContent]
  )
  const selectedIsImage = selectedNode ? isImageNode(selectedNode) : false
  const selectedImageIsPending =
    selectedNode?.status === "PENDING" || selectedNode?.status === "INDEXING"
  const selectedImageMediaUrl =
    selectedIsImage && selectedNode?.status === "READY"
      ? kb.getFileMediaUrl(selectedNode)
      : null
  const selectedImageDescription = useMemo(
    () =>
      selectedIsImage && selectedNode
        ? getImageSearchDescription(selectedContent, selectedNode.name)
        : "",
    [selectedContent, selectedIsImage, selectedNode]
  )
  const canExpandSelectedImageDescription =
    selectedImageDescription.length > 240 ||
    selectedImageDescription.split("\n").length > 4

  const selectedNodeId = selectedNode?.id
  const selectedNodeType = selectedNode?.node_type
  const selectedNodeStatus = selectedNode?.status
  const selectedNodeContentLoaded = selectedNode?.content_loaded
  const tabularLoadFailedTitle = t("knowledgeBase.treeView.tabular.loadFailed")
  const tabularCapabilitiesFailedDescription = t(
    "knowledgeBase.treeView.tabular.loadCapabilitiesFailed"
  )
  const selectedPotentialTabular = isPotentialTabularKnowledgeNode(selectedNode)
  const [tabularResolution, setTabularResolution] = useState<{
    nodeId: string
    enabled: boolean | null
    error?: boolean
  } | null>(null)
  const selectedTabularFeatureEnabled =
    selectedPotentialTabular &&
    tabularResolution?.nodeId === selectedNodeId &&
    tabularResolution?.enabled === true
  const selectedTabularResolutionPending =
    selectedPotentialTabular &&
    (tabularResolution?.nodeId !== selectedNodeId ||
      (tabularResolution?.enabled === null && !tabularResolution?.error))
  const selectedTabularResolutionError =
    selectedPotentialTabular &&
    tabularResolution?.nodeId === selectedNodeId &&
    tabularResolution?.error === true

  useEffect(() => {
    if (!selectedNodeId || !selectedPotentialTabular) {
      setTabularResolution(null)
      return
    }
    if (!currentOrganization?.id || !selectedNode?.workspace_id) return
    let active = true
    setTabularResolution({ nodeId: selectedNodeId, enabled: null })
    fetchTabularCapabilities({
      urlBuilderParams: {
        organizationId: currentOrganization.id,
        workspaceId: selectedNode.workspace_id,
      },
    })
      .then((response) => {
        if (!active) return
        setTabularResolution({
          nodeId: selectedNodeId,
          enabled: response?.data?.enabled === true,
        })
      })
      .catch((error) => {
        if (!active) return
        console.error("[KB] resolve tabular capability failed", error)
        setTabularResolution({
          nodeId: selectedNodeId,
          enabled: null,
          error: true,
        })
        alertToast({
          ...getUserFacingApiErrorToastProps(error, translationRef.current, {
            title: tabularLoadFailedTitle,
            fallbackDescription: tabularCapabilitiesFailedDescription,
          }),
        })
      })
    return () => {
      active = false
    }
  }, [
    currentOrganization?.id,
    alertToast,
    fetchTabularCapabilities,
    selectedNode?.workspace_id,
    selectedNodeId,
    selectedPotentialTabular,
    tabularCapabilitiesFailedDescription,
    tabularLoadFailedTitle,
  ])

  // Right-panel "current folder" (navigation) is decoupled from selection (single-click).
  // - `selectedNode` drives toolbar enable/disable and selection highlight
  // - `currentFolderId` drives which folder's contents are displayed in the right panel
  const [currentFolderId, setCurrentFolderId] = useState<string>(
    () => kb.root.id
  )
  // File preview is only shown when a file is explicitly "opened" (double click or selecting in left tree).
  const [openedFileId, setOpenedFileId] = useState<string | null>(null)
  const [isImageDescriptionExpanded, setIsImageDescriptionExpanded] =
    useState(false)

  useEffect(() => {
    setIsImageDescriptionExpanded(false)
  }, [openedFileId])

  /**
   * Auto-fetch file content the first time a READY file node is selected.
   * Guarded by `content_loaded` to prevent repeated requests.
   */
  useEffect(() => {
    if (!selectedNodeId) return
    if (selectedNodeType !== "FILE") return
    if (selectedNodeStatus !== "READY") return
    if (selectedPotentialTabular && tabularResolution?.enabled !== false) return
    if (selectedNodeContentLoaded) return
    // Avoid fetching content on "selection only"; only fetch when the file is explicitly opened.
    if (openedFileId !== selectedNodeId) return
    fetchFileContent(selectedNodeId).catch((e) => {
      console.error("[KB] fetchFileContent failed", e)
    })
  }, [
    fetchFileContent,
    openedFileId,
    selectedNodeContentLoaded,
    selectedNodeId,
    selectedNodeStatus,
    selectedNodeType,
    selectedPotentialTabular,
    tabularResolution?.enabled,
  ])

  // TreeView-specific state (local UI state)
  const [expandedNodes, setExpandedNodes] = useState<TreeNodeState>({})
  const [searchQuery, setSearchQuery] = useState("")

  // Dialog/flow state (TreeView-specific)
  const [isNewFolderDialogOpen, setIsNewFolderDialogOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState("")
  const [newFolderDescription, setNewFolderDescription] = useState("")
  const [
    pendingOpenCrawlerDialogFolderId,
    setPendingOpenCrawlerDialogFolderId,
  ] = useState<string | null>(null)
  // Parent folder id where "New folder" will be created.
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null)
  // Folder id that selected local files will be uploaded into.
  const [fileUploadTargetFolderId, setFileUploadTargetFolderId] = useState<
    string | null
  >(null)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const [uploadTabularCapabilities, setUploadTabularCapabilities] = useState<{
    enabled: boolean
    accepted_extensions: string[]
    accepted_mime_types: string[]
    max_source_bytes: number
  } | null>(null)
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)
  const [isDeletingNode, setIsDeletingNode] = useState(false)

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [nodesToDelete, setNodesToDelete] = useState<KnowledgeNode[]>([])

  // Detail panel edit state. Rename stays in the fixed header; description edits live in body metadata.
  const [isEditingDetails, setIsEditingDetails] = useState(false)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editName, setEditName] = useState("")
  const [editDescription, setEditDescription] = useState<string>("")
  const [isSavingDetails, setIsSavingDetails] = useState(false)
  const [isSavingDescription, setIsSavingDescription] = useState(false)
  const previousDisplayedNodeIdRef = useRef<string | null>(null)

  // Hidden file input used to open the native file picker from a custom "Upload" button.
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Toggle a folder row expanded/collapsed in the left tree panel.
  const toggleExpand = useCallback((nodeId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }))
  }, [])

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    // Collapse everything back to the root level (root row stays visible; children hidden).
    setExpandedNodes({})
  }, [])

  // Filter tree in-memory based on `searchQuery` (used by left-panel search).
  const filterNode = useCallback(
    (node: KnowledgeNode): KnowledgeNode | null => {
      if (!searchQuery.trim()) return node

      const query = searchQuery.toLowerCase()
      const nameMatches = node.name.toLowerCase().includes(query)
      const descMatches = node.description?.toLowerCase().includes(query)

      if (node.node_type === "FILE") {
        return nameMatches || descMatches ? node : null
      }

      // For folders, check children recursively
      const filteredChildren = node.children
        ?.map(filterNode)
        .filter((n): n is KnowledgeNode => n !== null)

      if (
        nameMatches ||
        descMatches ||
        (filteredChildren && filteredChildren.length > 0)
      ) {
        return {
          ...node,
          children: filteredChildren,
        }
      }

      return null
    },
    [searchQuery]
  )

  const filteredRoot = useMemo(() => filterNode(kb.root), [filterNode, kb.root])

  // Default-expand the root so workspace nodes are visually connected under it.
  useEffect(() => {
    setExpandedNodes((prev) => {
      if (prev[kb.root.id]) return prev
      return { ...prev, [kb.root.id]: true }
    })
  }, [kb.root.id])

  // Resolve a readable path for the "New Folder" dialog (e.g. "Workspace / Folder").
  const nodeById = useMemo(() => {
    const map = new Map<string, KnowledgeNode>()
    const dfs = (n: KnowledgeNode) => {
      map.set(n.id, n)
      for (const c of n.children || []) dfs(c)
    }
    dfs(kb.root)
    return map
  }, [kb.root])

  // Open the new-folder dialog for a given parent folder.
  // Ensure children are loaded first so duplicate checking works correctly.
  const handleNewFolder = useCallback(
    async (folderId: string) => {
      const folder = nodeById.get(folderId)
      if (folder && folder.node_type === "FOLDER" && !folder.children_loaded) {
        await kb.ensureChildrenLoaded(folderId)
      }
      setTargetFolderId(folderId)
      setNewFolderName("")
      setNewFolderDescription("")
      setIsNewFolderDialogOpen(true)
    },
    [kb, nodeById]
  )

  // Helper function to check if a name is duplicated in a folder's children
  const hasDuplicateName = useCallback(
    (
      folderId: string | null,
      name: string,
      excludeNodeId?: string
    ): boolean => {
      if (!folderId || !name.trim()) return false
      const folder = nodeById.get(folderId)
      if (!folder || !folder.children) return false
      const trimmedName = name.trim()
      return folder.children.some(
        (child) => child.id !== excludeNodeId && child.name === trimmedName
      )
    },
    [nodeById]
  )

  const currentFolderNode = useMemo(() => {
    const n = nodeById.get(currentFolderId)
    if (n && n.node_type === "FOLDER") return n
    return kb.root
  }, [currentFolderId, kb.root, nodeById])

  const currentFolderContentItems = useMemo(
    () => currentFolderNode.children ?? EMPTY_CHILDREN,
    [currentFolderNode.children]
  )

  const currentFolderItemOrderIds = useMemo(
    () => currentFolderContentItems.map((item) => item.id),
    [currentFolderContentItems]
  )
  const contentSelection = useContentSelection({
    scopeId: currentFolderNode.id,
    itemOrderIds: currentFolderItemOrderIds,
  })
  const selectedContentNodeById = useMemo(() => {
    const map = new Map<string, KnowledgeNode>()
    for (const item of currentFolderContentItems) {
      map.set(item.id, item)
    }
    return map
  }, [currentFolderContentItems])
  const selectedContentNodes = useMemo(
    () =>
      Array.from(contentSelection.selectedIds)
        .map((id) => selectedContentNodeById.get(id))
        .filter((node): node is KnowledgeNode => !!node),
    [contentSelection.selectedIds, selectedContentNodeById]
  )
  const primaryContentNode = contentSelection.primaryId
    ? selectedContentNodeById.get(contentSelection.primaryId) || null
    : null
  const currentWorkspaceId = currentFolderNode.workspace_id
  const currentOrganizationId = currentOrganization?.id
  const isCurrentFolderCrawlerEligible =
    currentFolderNode.node_type === "FOLDER" &&
    currentFolderNode.ui_kind !== "WORKSPACE" &&
    currentFolderNode.id !== kb.root.id &&
    !!currentWorkspaceId &&
    !!currentOrganizationId

  const websiteCrawlerDialog = useWebsiteCrawlerDialog({
    currentFolderNode,
    currentOrganizationId: currentOrganizationId || undefined,
    currentWorkspaceId: currentWorkspaceId || undefined,
    isCurrentFolderCrawlerEligible,
    refreshFolderChildren: kb.refreshFolderChildren,
    createWebsiteCrawl: kb.createWebsiteCrawl,
  })
  const openWebsiteCrawlerDialog = websiteCrawlerDialog.openDialog
  const [syncingCrawlerFolderIds, setSyncingCrawlerFolderIds] = useState<
    Set<string>
  >(() => new Set())

  useEffect(() => {
    const folderId = websiteCrawlerDialog.jobFolderId
    const jobStatus = websiteCrawlerDialog.job?.status
    if (!folderId || !jobStatus) return

    const isActive = ACTIVE_WEBSITE_CRAWL_JOB_STATUSES.has(jobStatus)
    setSyncingCrawlerFolderIds((prev) => {
      const alreadyTracked = prev.has(folderId)
      if (isActive) {
        if (alreadyTracked) return prev
        const next = new Set(prev)
        next.add(folderId)
        return next
      }
      if (!alreadyTracked) return prev
      const next = new Set(prev)
      next.delete(folderId)
      return next
    })
  }, [websiteCrawlerDialog.job?.status, websiteCrawlerDialog.jobFolderId])

  const isWebsiteCrawlerFolderSyncing = useCallback(
    (node: KnowledgeNode) =>
      node.node_type === "FOLDER" &&
      node.source_type === "WEBSITE_CRAWLER" &&
      syncingCrawlerFolderIds.has(node.id),
    [syncingCrawlerFolderIds]
  )

  const isOpenedFileSelected =
    selectedNode?.node_type === "FILE" && openedFileId === selectedNode.id
  const isMultiContentsSelection =
    !isOpenedFileSelected && contentSelection.selectedIds.size > 1

  useEffect(() => {
    if (!isMultiContentsSelection) return
    if (openedFileId !== null) setOpenedFileId(null)
  }, [isMultiContentsSelection, openedFileId])

  // Determine which node to display in the header and highlight in the left panel.
  // When viewing folder contents (not an opened file), show the folder.
  // When a file is opened, show the file.
  const displayedNode = useMemo(() => {
    if (isOpenedFileSelected) {
      return selectedNode
    }
    return currentFolderNode
  }, [isOpenedFileSelected, selectedNode, currentFolderNode])

  // Determine which node to highlight in the left panel.
  // When viewing folder contents, highlight the folder. Otherwise, highlight the selected node.
  const highlightedNode = useMemo(() => {
    if (isOpenedFileSelected) {
      return selectedNode
    }
    return currentFolderNode
  }, [isOpenedFileSelected, selectedNode, currentFolderNode])

  // Check if the displayed node is synthetic (for UI purposes)
  const isDisplayedSyntheticNode = useMemo(() => {
    if (!displayedNode) return false
    return (
      displayedNode.ui_kind === "WORKSPACE" || displayedNode.id === kb.root.id
    )
  }, [displayedNode, kb.root.id])

  const isDisplayedCrawlerFolder = useMemo(() => {
    if (!displayedNode) return false
    return (
      displayedNode.node_type === "FOLDER" &&
      displayedNode.source_type === "WEBSITE_CRAWLER"
    )
  }, [displayedNode])

  /**
   * Sync edit drafts with the displayed node.
   * We only sync when not in edit mode, to avoid overwriting user input mid-edit.
   */
  useEffect(() => {
    const displayedNodeId = displayedNode?.id ?? null
    if (isMultiContentsSelection) {
      setIsEditingDetails(false)
      setIsEditingDescription(false)
      previousDisplayedNodeIdRef.current = displayedNodeId
      return
    }
    if (!displayedNode) {
      setIsEditingDetails(false)
      setIsEditingDescription(false)
      setEditName("")
      setEditDescription("")
      previousDisplayedNodeIdRef.current = null
      return
    }
    if (previousDisplayedNodeIdRef.current !== displayedNodeId) {
      setIsEditingDetails(false)
      setIsEditingDescription(false)
      setEditName(displayedNode.name || "")
      setEditDescription(displayedNode.description || "")
      previousDisplayedNodeIdRef.current = displayedNodeId
      return
    }
    if (isEditingDetails || isEditingDescription) return
    setEditName(displayedNode.name || "")
    setEditDescription(displayedNode.description || "")
    previousDisplayedNodeIdRef.current = displayedNodeId
  }, [
    isEditingDescription,
    isEditingDetails,
    displayedNode,
    isMultiContentsSelection,
  ])

  // Enter edit mode for the displayed node (workspace nodes are UI-only and not editable).
  // Ensure parent folder's children are loaded first so duplicate checking works correctly.
  const startEditDetails = useCallback(async () => {
    if (!displayedNode) return
    if (displayedNode.ui_kind === "WORKSPACE") return
    // Load parent folder's children if not already loaded
    if (displayedNode.parent_id) {
      const parent = nodeById.get(displayedNode.parent_id)
      if (parent && parent.node_type === "FOLDER" && !parent.children_loaded) {
        await kb.ensureChildrenLoaded(displayedNode.parent_id)
      }
    }
    setEditName(displayedNode.name || "")
    setIsEditingDescription(false)
    setIsEditingDetails(true)
  }, [displayedNode, kb, nodeById])

  // Exit edit mode and reset drafts back to the displayed node's latest values.
  const cancelEditDetails = useCallback(() => {
    if (!displayedNode) {
      setIsEditingDetails(false)
      return
    }
    setEditName(displayedNode.name || "")
    setIsEditingDetails(false)
  }, [displayedNode])

  // Check for duplicate name in edit mode (excluding the current node)
  const hasDuplicateNameInEdit = useMemo(() => {
    if (!displayedNode || !isEditingDetails) return false
    const parentId = displayedNode.parent_id
    return hasDuplicateName(parentId || null, editName, displayedNode.id)
  }, [displayedNode, editName, hasDuplicateName, isEditingDetails])

  const startEditDescription = useCallback(() => {
    if (!displayedNode) return
    if (displayedNode.ui_kind === "WORKSPACE") return
    setEditDescription(displayedNode.description || "")
    setIsEditingDetails(false)
    setIsEditingDescription(true)
  }, [displayedNode])

  const cancelEditDescription = useCallback(() => {
    if (!displayedNode) {
      setIsEditingDescription(false)
      return
    }
    setEditDescription(displayedNode.description || "")
    setIsEditingDescription(false)
  }, [displayedNode])

  // Persist name changes via the controller hook and show a toast on failure.
  const saveEditDetails = useCallback(async () => {
    if (!displayedNode) return
    if (isSavingDetails) return
    const name = editName.trim()
    if (!name) return
    setIsSavingDetails(true)
    try {
      await kb.updateNode(displayedNode.id, {
        name,
      })
      setIsEditingDetails(false)
    } catch (e) {
      console.error("[KB] updateNode failed", e)
      alertToast({
        title: t("knowledgeBase.treeView.updateNodeTitle"),
        description: t("knowledgeBase.treeView.updateNodeFailed"),
      })
    } finally {
      setIsSavingDetails(false)
    }
  }, [alertToast, editName, isSavingDetails, kb, displayedNode, t])

  const saveEditDescription = useCallback(async () => {
    if (!displayedNode) return
    if (isSavingDescription) return
    setIsSavingDescription(true)
    try {
      await kb.updateNode(displayedNode.id, {
        description: editDescription,
      })
      setIsEditingDescription(false)
    } catch (e) {
      console.error("[KB] updateNode description failed", e)
      alertToast({
        title: t("knowledgeBase.treeView.updateNodeTitle"),
        description: t("knowledgeBase.treeView.updateNodeFailed"),
      })
    } finally {
      setIsSavingDescription(false)
    }
  }, [alertToast, editDescription, isSavingDescription, kb, displayedNode, t])

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault()
        if (!editName.trim() || hasDuplicateNameInEdit) return
        void saveEditDetails()
      }
      if (e.key === "Escape") {
        e.preventDefault()
        cancelEditDetails()
      }
    },
    [cancelEditDetails, editName, hasDuplicateNameInEdit, saveEditDetails]
  )

  const handleDetailBackgroundClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (contentSelection.consumeSuppressClick()) return
      const target = e.target
      if (!(target instanceof Element)) return
      // Ignore clicks on interactive content (items, links, inputs, etc.).
      if (target.closest("button, a, input, textarea, [role='button']")) return
      if (openedFileId === selectedNodeId) return

      // Clicking the empty area clears the current selection back to the current folder.
      contentSelection.clearSelection()
      kb.selectNode(currentFolderNode)
      setOpenedFileId(null)
      setIsEditingDetails(false)
    },
    [contentSelection, currentFolderNode, openedFileId, selectedNodeId, kb]
  )

  const targetFolderPath = useMemo(() => {
    if (!targetFolderId) return ""
    const parts: string[] = []
    let curId: string | null | undefined = targetFolderId
    // Walk up via parent_id, stopping before the synthetic root.
    while (curId && curId !== kb.root.id) {
      const n = nodeById.get(curId)
      if (!n) break
      parts.push(n.name)
      curId = n.parent_id
    }
    return parts.reverse().join(" / ")
  }, [kb.root.id, nodeById, targetFolderId])

  // Start local-upload flow: remember target folder, then open the native file picker.
  const handleUploadClick = useCallback(
    async (folderId: string) => {
      const folder = nodeById.get(folderId)
      const workspaceId = folder?.workspace_id
      setFileUploadTargetFolderId(folderId)
      if (!workspaceId || !currentOrganization?.id) {
        fileInputRef.current?.click()
        return
      }
      try {
        const response = await fetchTabularCapabilities({
          urlBuilderParams: {
            organizationId: currentOrganization.id,
            workspaceId,
          },
        })
        const capabilities = response?.data ?? null
        setUploadTabularCapabilities(capabilities)
        if (capabilities?.enabled) {
          setIsUploadDialogOpen(true)
        } else {
          fileInputRef.current?.click()
        }
      } catch (error) {
        console.error("[KB] load upload capabilities failed", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("knowledgeBase.treeView.uploadFile"),
            fallbackDescription: t(
              "knowledgeBase.treeView.tabular.loadCapabilitiesFailed"
            ),
          }),
        })
        setFileUploadTargetFolderId(null)
      }
    },
    [alertToast, currentOrganization?.id, fetchTabularCapabilities, nodeById, t]
  )

  const kbDropUpload = useKnowledgeBaseDropUpload({
    kb,
    currentFolderNode,
    openedFileId,
    expandedNodes,
    setExpandedNodes,
  })
  const dropErrorKey = kbDropUpload.dropErrorKey
  const clearDropErrorKey = kbDropUpload.clearDropErrorKey

  useEffect(() => {
    if (!dropErrorKey) return
    alertToast({
      title: t("knowledgeBase.treeView.uploadFile"),
      description: t(dropErrorKey),
    })
    clearDropErrorKey()
  }, [alertToast, clearDropErrorKey, dropErrorKey, t])

  /**
   * Selection handler for the left tree.
   * For folders/workspaces, we lazy-load children so the right panel can render contents immediately.
   * If in edit mode and selecting a different node, exit edit mode first.
   */
  const handleSelectNode = useCallback(
    (node: KnowledgeNode) => {
      // If in edit mode and selecting a different node, exit edit mode first
      if (isEditingDetails && selectedNode?.id !== node.id) {
        setIsEditingDetails(false)
      }
      contentSelection.clearSelection()
      kb.selectNode(node)
      // Left tree selection behaves like "open" (navigation).
      if (node.node_type === "FILE") {
        setOpenedFileId(node.id)
        if (node.parent_id) setCurrentFolderId(node.parent_id)
      } else {
        setOpenedFileId(null)
        setCurrentFolderId(node.id)
      }
      if (node.node_type === "FOLDER" && !node.children_loaded) {
        void kb.ensureChildrenLoaded(node.id)
      }
    },
    [contentSelection, isEditingDetails, kb, selectedNode]
  )

  /**
   * Right-panel: single click selects (updates toolbar + highlight) but does NOT navigate.
   */
  const selectFromContents = useCallback(
    (node: KnowledgeNode, e: React.MouseEvent<HTMLElement>) => {
      const isToggle = e.metaKey || e.ctrlKey
      const wasSelected = contentSelection.selectedIds.has(node.id)
      const willDeselectLastSelected =
        isToggle && wasSelected && contentSelection.selectedIds.size === 1

      if (isEditingDetails && selectedNode?.id !== node.id) {
        setIsEditingDetails(false)
      }
      contentSelection.handleItemClick(node.id, e)
      if (willDeselectLastSelected) {
        kb.selectNode(currentFolderNode)
        setOpenedFileId(null)
        return
      }
      kb.selectNode(node)
    },
    [contentSelection, currentFolderNode, isEditingDetails, kb, selectedNode]
  )

  /**
   * Right-panel: double click opens/enters.
   * - Folder: navigate into it and load children
   * - File: open preview (content fetch is gated by `openedFileId`)
   */
  const openFromContents = useCallback(
    async (node: KnowledgeNode) => {
      if (isEditingDetails && selectedNode?.id !== node.id) {
        setIsEditingDetails(false)
      }
      contentSelection.selectOnly(node.id)
      kb.selectNode(node)

      if (node.node_type === "FOLDER") {
        setOpenedFileId(null)
        setCurrentFolderId(node.id)
        if (!node.children_loaded) {
          await kb.ensureChildrenLoaded(node.id)
        }
        // Reveal in left tree (expand parent and the folder itself).
        setExpandedNodes((prev) => {
          const next: TreeNodeState = { ...prev }
          if (node.parent_id) next[node.parent_id] = true
          next[node.id] = true
          return next
        })
        return
      }

      setOpenedFileId(node.id)
      // Keep `currentFolderId` unchanged (stay in the current folder view).
    },
    [contentSelection, isEditingDetails, kb, selectedNode]
  )

  // Handle native file picker results and upload selected files to `fileUploadTargetFolderId`.
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0 && fileUploadTargetFolderId) {
        try {
          await kb.addFiles(fileUploadTargetFolderId, Array.from(files))
        } catch (err) {
          console.error("[KB] upload failed", err)
          alertToast({
            title: t("knowledgeBase.treeView.uploadFile"),
            description: "Upload failed.",
          })
        }
      }
      // Reset so selecting the same file again still triggers `onChange`.
      e.target.value = ""
      setFileUploadTargetFolderId(null)
      setIsUploadDialogOpen(false)
    },
    [alertToast, fileUploadTargetFolderId, kb, t]
  )

  // Open delete confirmation dialog for node(s).
  const handleDeleteClick = useCallback((nodes: KnowledgeNode[]) => {
    if (nodes.length === 0) return
    setNodesToDelete(nodes)
    setIsDeleteDialogOpen(true)
  }, [])

  const toolbarPrimaryNode =
    !isOpenedFileSelected && primaryContentNode
      ? primaryContentNode
      : selectedNode
  const contextualSelectionNodes = useMemo(() => {
    if (!isOpenedFileSelected) return selectedContentNodes
    return selectedNode?.node_type === "FILE" ? [selectedNode] : []
  }, [isOpenedFileSelected, selectedContentNodes, selectedNode])
  const hasContextualSelection = contextualSelectionNodes.length > 0
  const selectedWorkspace = (storeWorkspaces ?? []).find(
    (workspace) => workspace.id === selectedNode?.workspace_id
  )
  const canEditSelectedWorkspace = hasPermission(
    selectedWorkspace?.myPermissions ?? new Set<string>(),
    Permission.WsUpdateResources
  )

  // Toolbar "New Folder" is only enabled when a workspace node is selected.
  const isSelectedWorkspaceNode = toolbarPrimaryNode?.ui_kind === "WORKSPACE"

  const handleToolbarNewFolder = useCallback(() => {
    if (!toolbarPrimaryNode || toolbarPrimaryNode.ui_kind !== "WORKSPACE")
      return
    handleNewFolder(toolbarPrimaryNode.id)
  }, [handleNewFolder, toolbarPrimaryNode])

  const handleToolbarCreateWebsiteCrawl = useCallback(() => {
    if (!toolbarPrimaryNode || toolbarPrimaryNode.ui_kind !== "WORKSPACE")
      return
    websiteCrawlerDialog.openCreateDialog()
  }, [toolbarPrimaryNode, websiteCrawlerDialog])

  const isSelectedFolderNode =
    toolbarPrimaryNode?.node_type === "FOLDER" &&
    toolbarPrimaryNode?.ui_kind !== "WORKSPACE" &&
    toolbarPrimaryNode?.id !== kb.root.id

  const isSelectedFileNode = toolbarPrimaryNode?.node_type === "FILE"

  // Determine the target folder for "Add file" action:
  // - If a folder is selected, use the folder itself
  // - If a file is selected, use the file's parent folder
  // - Otherwise, null (button disabled)
  const addFileTargetFolderId = useMemo(() => {
    if (!toolbarPrimaryNode) return null
    if (isSelectedFolderNode) return toolbarPrimaryNode.id
    if (isSelectedFileNode && toolbarPrimaryNode.parent_id)
      return toolbarPrimaryNode.parent_id
    return null
  }, [isSelectedFileNode, isSelectedFolderNode, toolbarPrimaryNode])
  const addFileStorageAtLimit = kb.isKbStorageAtLimitForWorkspaceId(
    toolbarPrimaryNode?.workspace_id
  )
  const knowledgeBaseStorage = kb.knowledgeBaseStorage
  const kbStoragePercent =
    knowledgeBaseStorage?.bytesLimit && knowledgeBaseStorage.bytesLimit > 0
      ? Math.min(
          100,
          Math.round(
            (knowledgeBaseStorage.bytesUsed / knowledgeBaseStorage.bytesLimit) *
              100
          )
        )
      : null
  const addFileDisabledReason = addFileStorageAtLimit
    ? t("knowledgeBase.treeView.storageLimitReached")
    : undefined

  const handleToolbarDelete = useCallback(() => {
    const candidates = contextualSelectionNodes.filter((node) => {
      if (node.ui_kind === "WORKSPACE") return false
      if (node.id === kb.root.id) return false
      if (!isOpenedFileSelected && node.id === currentFolderNode.id)
        return false
      return true
    })
    if (candidates.length === 0) return
    handleDeleteClick(candidates)
  }, [
    handleDeleteClick,
    isOpenedFileSelected,
    currentFolderNode.id,
    kb.root.id,
    contextualSelectionNodes,
  ])

  const hasFolderInSelection = contextualSelectionNodes.some(
    (node) => node.node_type === "FOLDER"
  )
  const downloadableSelection = contextualSelectionNodes.filter(
    (node) => node.node_type === "FILE"
  )
  const canDownloadSelection =
    downloadableSelection.length > 0 &&
    downloadableSelection.length === contextualSelectionNodes.length

  const handleToolbarDownload = useCallback(() => {
    if (!canDownloadSelection) return
    if (downloadableSelection.length === 1) {
      void kb.downloadFile(downloadableSelection[0])
      return
    }
    void kb.downloadFilesAsZip(downloadableSelection)
  }, [canDownloadSelection, downloadableSelection, kb])

  const hasTextSnapshotDownload = downloadableSelection.some(
    (node) => node.source_type !== "LOCAL_UPLOAD"
  )

  const downloadDisabledReason = hasFolderInSelection
    ? t("knowledgeBase.treeView.downloadDisabledContainsFolder")
    : undefined
  const canDeleteSelection = contextualSelectionNodes.some((node) => {
    if (node.ui_kind === "WORKSPACE") return false
    if (node.id === kb.root.id) return false
    if (!isOpenedFileSelected && node.id === currentFolderNode.id) return false
    return true
  })

  const handleToolbarGoParent = useCallback(() => {
    const parentFolderNode = currentFolderNode.parent_id
      ? nodeById.get(currentFolderNode.parent_id) || null
      : null
    const targetNode = isOpenedFileSelected
      ? currentFolderNode
      : parentFolderNode
    if (!targetNode) return
    kb.selectNode(targetNode)
    setCurrentFolderId(targetNode.id)
    setOpenedFileId(null)
  }, [currentFolderNode, isOpenedFileSelected, kb, nodeById])

  // Check for duplicate name in create folder dialog
  const hasDuplicateNameInCreate = useMemo(() => {
    if (!targetFolderId || !newFolderName.trim()) return false
    return hasDuplicateName(targetFolderId, newFolderName)
  }, [targetFolderId, newFolderName, hasDuplicateName])

  // Create a new folder under `targetFolderId` and expand the parent on success.
  const handleConfirmNewFolder = useCallback(() => {
    if (!targetFolderId || !newFolderName.trim()) return
    if (isCreatingFolder) return
    setIsCreatingFolder(true)
    kb.createFolder(
      targetFolderId,
      newFolderName.trim(),
      newFolderDescription.trim() ? newFolderDescription.trim() : null
    )
      .then(() => {
        setExpandedNodes((prev) => ({ ...prev, [targetFolderId]: true }))
        setIsNewFolderDialogOpen(false)
        setNewFolderName("")
        setNewFolderDescription("")
        setTargetFolderId(null)
      })
      .catch((e) => {
        console.error("[KB] createFolder failed", e)
        alertToast({
          title: t("knowledgeBase.treeView.newFolderTitle"),
          description: "Failed to create folder.",
        })
      })
      .finally(() => setIsCreatingFolder(false))
  }, [
    alertToast,
    isCreatingFolder,
    kb,
    newFolderDescription,
    newFolderName,
    t,
    targetFolderId,
  ])

  // Delete selected nodes and clear selection state after completion.
  const handleConfirmDelete = useCallback(() => {
    if (nodesToDelete.length === 0) return
    if (isDeletingNode) return
    setIsDeletingNode(true)
    const deletingIds = nodesToDelete.map((node) => node.id)
    Promise.allSettled(deletingIds.map((nodeId) => kb.deleteNode(nodeId)))
      .then((results) => {
        const failedCount = results.filter(
          (r) => r.status === "rejected"
        ).length
        if (selectedNode?.id && deletingIds.includes(selectedNode.id)) {
          kb.selectNode(null)
        }
        contentSelection.clearSelection()
        if (failedCount > 0) {
          alertToast({
            title: t("knowledgeBase.treeView.deleteTitle"),
            description: `Failed to delete ${failedCount} item(s).`,
          })
        }
        setIsDeleteDialogOpen(false)
        setNodesToDelete([])
      })
      .catch((e) => {
        console.error("[KB] delete failed", e)
        alertToast({
          title: t("knowledgeBase.treeView.deleteTitle"),
          description: "Failed to delete.",
        })
      })
      .finally(() => setIsDeletingNode(false))
  }, [
    alertToast,
    contentSelection,
    isDeletingNode,
    kb,
    nodesToDelete,
    selectedNode,
    t,
  ])

  const handleConfirmCreateWebsiteCrawl = useCallback(async () => {
    if (!selectedNode || selectedNode.ui_kind !== "WORKSPACE") return

    const createdNode = await websiteCrawlerDialog.createFromWorkspace(
      selectedNode.id
    )
    if (!createdNode) return

    kb.selectNode(createdNode)
    setCurrentFolderId(createdNode.id)
    setOpenedFileId(null)
    setPendingOpenCrawlerDialogFolderId(createdNode.id)
  }, [kb, selectedNode, websiteCrawlerDialog])

  const handleOpenWebsiteSyncSettingsForNode = useCallback(
    (node: KnowledgeNode) => {
      if (
        node.node_type !== "FOLDER" ||
        node.source_type !== "WEBSITE_CRAWLER" ||
        node.ui_kind === "WORKSPACE" ||
        node.id === kb.root.id
      ) {
        return
      }
      kb.selectNode(node)
      setCurrentFolderId(node.id)
      setOpenedFileId(null)
      setPendingOpenCrawlerDialogFolderId(node.id)
    },
    [kb]
  )

  useEffect(() => {
    if (!pendingOpenCrawlerDialogFolderId) return
    if (currentFolderNode.id !== pendingOpenCrawlerDialogFolderId) return
    openWebsiteCrawlerDialog()
    setPendingOpenCrawlerDialogFolderId(null)
  }, [
    currentFolderNode.id,
    openWebsiteCrawlerDialog,
    pendingOpenCrawlerDialogFolderId,
  ])

  return (
    <div className="flex h-[calc(100vh-260px)] min-h-[480px] w-full overflow-hidden rounded-md border border-border bg-background">
      {/* Tree Overview panel */}
      <div
        className="w-80 flex flex-col border-r border-border bg-background"
        data-testid="kb-tree-panel"
        {...kbDropUpload.treePanelDragHandlers}
      >
        {/* Overview Header */}
        <div className="flex h-14 items-center border-b border-border bg-muted/30 px-3">
          {/* Search */}
          <div className="flex w-full items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("knowledgeBase.treeView.searchPlaceholder")}
                className="pl-8 pr-8 h-8 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                  title={t("knowledgeBase.treeView.clearSearch")}
                  aria-label={t("knowledgeBase.treeView.clearSearch")}
                >
                  <X className="w-3 h-3 text-slate-400" />
                </button>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={collapseAll}
              className="h-8 w-8 p-0 flex-shrink-0"
              title={t("knowledgeBase.treeView.collapseAll")}
              aria-label={t("knowledgeBase.treeView.collapseAll")}
            >
              <ListCollapse className="w-4 h-4 text-slate-500 dark:text-slate-300" />
            </Button>
          </div>
        </div>

        {/* Overview Tree content */}
        <div className="flex-1 overflow-auto p-2">
          {filteredRoot ? (
            <div>
              <TreeNode
                rootId={kb.root.id}
                node={filteredRoot}
                depth={0}
                expandedNodes={expandedNodes}
                selectedNode={highlightedNode}
                onToggleExpand={toggleExpand}
                onSelect={handleSelectNode}
                onNewFolder={handleNewFolder}
                onUpload={handleUploadClick}
                connections={myConnections}
                isStorageAtLimit={kb.isKbStorageAtLimitForWorkspaceId(
                  filteredRoot.workspace_id
                )}
                onAddFromGoogleDriveConnection={(folderId, connectionId) =>
                  kb.addFromGoogleDrive(folderId, connectionId)
                }
                onAddFromNotionConnection={(folderId, connectionId) =>
                  kb.addFromNotionConnection(folderId, connectionId)
                }
                onConnectAnotherGoogleAccount={(folderId) =>
                  kb.connectAnotherGoogleAccount(folderId)
                }
                onConnectAnotherNotionWorkspace={(folderId) =>
                  kb.connectAnotherNotionWorkspace(folderId)
                }
                onDeleteConnection={(connectionId) =>
                  kb.deleteConnection(connectionId)
                }
                onDelete={(node) => handleDeleteClick([node])}
                onOpenWebsiteSyncSettings={handleOpenWebsiteSyncSettingsForNode}
                isWebsiteCrawlerFolderSyncing={isWebsiteCrawlerFolderSyncing}
                onEnsureChildrenLoaded={kb.ensureChildrenLoaded}
                onDownload={kb.downloadFile}
                dragHandlers={kbDropUpload.getTreeNodeDragHandlers}
                activeDropTargetNodeId={kbDropUpload.activeTreeDropTargetId}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-slate-400">
              <Search className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">{t("knowledgeBase.treeView.noResults")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      <div
        className="relative flex-1 flex flex-col bg-background overflow-x-hidden"
        data-testid="kb-detail-panel"
      >
        {knowledgeBaseStorage && (
          <div className="relative flex h-14 items-center overflow-hidden border-b border-border bg-muted/20 px-4 pb-1.5">
            <div className="flex w-full items-center justify-between gap-4 text-xs">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 font-medium text-foreground">
                  <span>{t("knowledgeBase.treeView.storageUsage")}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        aria-label={t(
                          "knowledgeBase.treeView.storageUsageDescription"
                        )}
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      className="max-w-72 text-left leading-relaxed"
                    >
                      {t("knowledgeBase.treeView.storageUsageDescription")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <div className="flex min-w-0 items-center justify-end gap-2">
                <span className="whitespace-nowrap text-muted-foreground">
                  {knowledgeBaseStorage.bytesLimit != null
                    ? t("knowledgeBase.treeView.storageUsageValue", {
                        used: formatBytes(knowledgeBaseStorage.bytesUsed),
                        limit: formatBytes(knowledgeBaseStorage.bytesLimit),
                      })
                    : t("knowledgeBase.treeView.storageUsageUnlimited", {
                        used: formatBytes(knowledgeBaseStorage.bytesUsed),
                      })}
                </span>
                {kb.isKbStorageAtLimit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-red-600 transition-colors hover:text-red-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:text-red-400 dark:hover:text-red-300"
                        aria-label={t(
                          "knowledgeBase.treeView.storageActionsDisabled"
                        )}
                      >
                        <TriangleAlert className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="end"
                      className="max-w-72 text-left leading-relaxed"
                    >
                      {t("knowledgeBase.treeView.storageActionsDisabled")}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
            {kbStoragePercent != null && (
              <div
                className="absolute inset-x-4 bottom-1 h-1 rounded-full bg-muted"
                role="progressbar"
                aria-label={t("knowledgeBase.treeView.storageUsage")}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={kbStoragePercent}
              >
                <div
                  className={cn(
                    "h-1 rounded-full transition-all",
                    knowledgeBaseStorage.atLimit
                      ? "bg-red-500"
                      : kbStoragePercent >= 80
                        ? "bg-amber-500"
                        : "bg-emerald-500"
                  )}
                  style={{ width: `${kbStoragePercent}%` }}
                />
              </div>
            )}
          </div>
        )}
        {displayedNode ? (
          <>
            {/* Detail header */}
            <div
              className={cn(
                "relative shrink-0 overflow-x-hidden border-b border-border bg-background px-4",
                isDisplayedCrawlerFolder ? "min-h-[60px] py-2" : "h-[60px] py-0"
              )}
            >
              <div className="flex h-full items-center gap-3 min-w-0">
                <div
                  className={cn(
                    "w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0",
                    displayedNode.ui_kind === "WORKSPACE"
                      ? "bg-slate-100 dark:bg-slate-800"
                      : displayedNode.node_type === "FOLDER"
                        ? displayedNode.source_type === "WEBSITE_CRAWLER"
                          ? "bg-cyan-100 dark:bg-cyan-900/35"
                          : "bg-amber-100 dark:bg-amber-900/30"
                        : "bg-blue-100 dark:bg-blue-900/30"
                  )}
                >
                  {displayedNode.ui_kind === "WORKSPACE" ? (
                    <BriefcaseBusiness className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                  ) : displayedNode.node_type === "FOLDER" ? (
                    displayedNode.source_type === "WEBSITE_CRAWLER" ? (
                      isWebsiteCrawlerFolderSyncing(displayedNode) ? (
                        <Loader2 className="w-5 h-5 animate-spin text-cyan-700 dark:text-cyan-200" />
                      ) : (
                        <Globe className="w-5 h-5 text-cyan-700 dark:text-cyan-200" />
                      )
                    ) : (
                      <FolderOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    )
                  ) : (
                    renderNodeIcon(displayedNode, {
                      className: "w-5 h-5",
                      wrapperClassName: "w-5 h-5",
                    })
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5 overflow-hidden">
                  {isEditingDetails ? (
                    <>
                      <div className="pr-40">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={handleRenameKeyDown}
                          aria-label={t("knowledgeBase.treeView.folderName")}
                          aria-invalid={hasDuplicateNameInEdit}
                          title={
                            hasDuplicateNameInEdit
                              ? t("knowledgeBase.treeView.duplicateNameWarning")
                              : undefined
                          }
                          className={cn(
                            "h-8 focus-visible:ring-1 focus-visible:ring-ring",
                            hasDuplicateNameInEdit &&
                              "border-red-500 focus-visible:border-red-500"
                          )}
                        />
                      </div>
                      <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 gap-1"
                          onClick={saveEditDetails}
                          disabled={
                            isSavingDetails ||
                            !editName.trim() ||
                            hasDuplicateNameInEdit
                          }
                          title={t("knowledgeBase.treeView.save")}
                          aria-label={t("knowledgeBase.treeView.save")}
                        >
                          <Check className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                          <span className="text-xs">
                            {t("knowledgeBase.treeView.save")}
                          </span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 gap-1"
                          onClick={cancelEditDetails}
                          disabled={isSavingDetails}
                          title={t("knowledgeBase.treeView.cancel")}
                          aria-label={t("knowledgeBase.treeView.cancel")}
                        >
                          <X className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                          <span className="text-xs">
                            {t("knowledgeBase.treeView.cancel")}
                          </span>
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className={cn(
                          "flex min-h-6 items-center gap-2 min-w-0 max-w-full",
                          !isDisplayedSyntheticNode &&
                            (isDisplayedCrawlerFolder ? "pr-20" : "pr-10")
                        )}
                      >
                        <h3
                          className="font-medium text-slate-900 dark:text-slate-100 truncate min-w-0 flex-1 leading-6"
                          title={displayedNode.name}
                        >
                          {displayedNode.name}
                        </h3>
                        {isDisplayedCrawlerFolder && (
                          <span className="inline-flex items-center rounded-full border border-cyan-300 bg-cyan-100 px-2 py-0.5 text-xs text-cyan-800 dark:border-cyan-700 dark:bg-cyan-900/35 dark:text-cyan-200">
                            {t("knowledgeBase.treeView.crawlWebsite")}
                          </span>
                        )}
                      </div>
                      <div
                        className={cn(
                          "flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs leading-4 text-slate-500 dark:text-slate-400",
                          isDisplayedCrawlerFolder ? "pr-20" : "pr-10"
                        )}
                      >
                        <span>
                          {displayedNode.ui_kind === "WORKSPACE"
                            ? t("knowledgeBase.treeView.workspaceType")
                            : displayedNode.node_type === "FOLDER"
                              ? t("knowledgeBase.treeView.folderType")
                              : isImageNode(displayedNode)
                                ? t("knowledgeBase.treeView.imageType")
                                : t("knowledgeBase.treeView.documentType")}
                          {displayedNode.node_type === "FOLDER" &&
                            ` • ${displayedNode.children?.length || 0} ${t("knowledgeBase.treeView.items")}`}
                        </span>
                        {isDisplayedCrawlerFolder && (
                          <>
                            <span aria-hidden="true">·</span>
                            <WebsiteCrawlSummary
                              target={websiteCrawlerDialog.target}
                              job={websiteCrawlerDialog.job}
                              status={websiteCrawlerDialog.stateStatus}
                              error={websiteCrawlerDialog.stateError}
                              onRetry={() => {
                                void websiteCrawlerDialog.refreshState()
                              }}
                              onDetails={websiteCrawlerDialog.openDialog}
                            />
                          </>
                        )}
                      </div>
                      {!isDisplayedSyntheticNode && (
                        <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-1">
                          {isDisplayedCrawlerFolder && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 gap-1"
                              onClick={websiteCrawlerDialog.openDialog}
                              title={t(
                                "knowledgeBase.treeView.websiteCrawlSettings"
                              )}
                              aria-label={t(
                                "knowledgeBase.treeView.websiteCrawlSettings"
                              )}
                            >
                              <Settings className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 gap-1"
                            onClick={startEditDetails}
                            title={t("knowledgeBase.treeView.edit")}
                            aria-label={t("knowledgeBase.treeView.edit")}
                          >
                            <Pencil className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Detail toolbar */}
            <div className="flex h-11 items-center overflow-x-auto border-b border-border bg-muted/30 px-3 py-1.5">
              <div className="flex h-8 shrink-0 items-center border-r border-border pr-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToolbarGoParent}
                  disabled={
                    !(
                      isOpenedFileSelected ||
                      (currentFolderNode.parent_id &&
                        nodeById.get(currentFolderNode.parent_id))
                    )
                  }
                  className="h-8 w-8 p-0"
                  title={t("knowledgeBase.treeView.goParent")}
                  aria-label={t("knowledgeBase.treeView.goParent")}
                >
                  <FolderUp className="w-4 h-4" />
                </Button>
              </div>

              <div className="ml-2 flex h-8 shrink-0 items-center gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <span title={addFileDisabledReason}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={
                          !addFileTargetFolderId || addFileStorageAtLimit
                        }
                        className="h-8 px-2 gap-1.5 font-medium"
                      >
                        <FilePlus2 className="w-4 h-4" />
                        <span className="text-sm">
                          {t("knowledgeBase.treeView.addFile")}
                        </span>
                      </Button>
                    </span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-60">
                    {addFileTargetFolderId ? (
                      <AddFileMenuItems
                        folderId={addFileTargetFolderId}
                        connections={
                          (myConnections as unknown as Array<{
                            id: string
                            name: string
                            status: string
                            provider: string
                          }>) || []
                        }
                        isStorageAtLimit={addFileStorageAtLimit}
                        onUpload={handleUploadClick}
                        onAddFromGoogleDriveConnection={(
                          folderId,
                          connectionId
                        ) => kb.addFromGoogleDrive(folderId, connectionId)}
                        onAddFromNotionConnection={(folderId, connectionId) =>
                          kb.addFromNotionConnection(folderId, connectionId)
                        }
                        onConnectAnotherGoogleAccount={(folderId) =>
                          kb.connectAnotherGoogleAccount(folderId)
                        }
                        onConnectAnotherNotionWorkspace={(folderId) =>
                          kb.connectAnotherNotionWorkspace(folderId)
                        }
                        onDeleteConnection={(connectionId) =>
                          kb.deleteConnection(connectionId)
                        }
                      />
                    ) : (
                      <div className="px-2 py-1 text-sm text-slate-400">
                        {t("knowledgeBase.treeView.selectItem")}
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!isSelectedWorkspaceNode}
                      className="h-8 px-2 gap-1.5"
                      title={t("knowledgeBase.treeView.newFolderActions")}
                      aria-label={t("knowledgeBase.treeView.newFolderActions")}
                    >
                      <FolderPlus className="w-4 h-4" />
                      <span className="text-sm">
                        {t("knowledgeBase.treeView.new")}
                      </span>
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-52">
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={handleToolbarNewFolder}
                    >
                      <FolderPlus className="w-4 h-4" />
                      {t("knowledgeBase.treeView.newFolder")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={handleToolbarCreateWebsiteCrawl}
                    >
                      <Globe className="w-4 h-4" />
                      {t("knowledgeBase.treeView.newWebsiteCrawl")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {hasContextualSelection && (
                <div className="ml-auto flex h-8 shrink-0 items-center gap-0.5 rounded-md border border-border bg-background px-1">
                  <span className="border-r border-border px-2 text-xs leading-none text-muted-foreground">
                    {t("knowledgeBase.treeView.selectedCount", {
                      count: contextualSelectionNodes.length,
                    })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleToolbarDownload}
                    disabled={!canDownloadSelection}
                    className="h-7 px-2 gap-1.5"
                    title={
                      downloadDisabledReason ||
                      (hasTextSnapshotDownload
                        ? t("knowledgeBase.treeView.downloadTextSnapshotHint")
                        : undefined)
                    }
                  >
                    <Download className="w-4 h-4" />
                    <span className="text-sm">
                      {t(
                        getKnowledgeBaseDownloadLabelKey(
                          contextualSelectionNodes
                        )
                      )}
                    </span>
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleToolbarDelete}
                    disabled={!canDeleteSelection}
                    className="h-7 px-2 gap-1.5 text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">
                      {t("knowledgeBase.treeView.delete")}
                    </span>
                  </Button>
                </div>
              )}
            </div>

            {/* Detail content */}
            <div
              className="relative flex-1 min-h-0"
              data-testid="kb-detail-content"
              {...kbDropUpload.detailPanelDragHandlers}
            >
              {kbDropUpload.detailDropOverlayState && (
                <div className="pointer-events-none absolute inset-0 z-20 rounded-lg border-2 border-dashed border-blue-400/70 bg-blue-100/20 dark:bg-blue-900/20" />
              )}
              <div
                className="h-full overflow-auto p-4"
                onClick={handleDetailBackgroundClick}
                onMouseDown={
                  isOpenedFileSelected
                    ? undefined
                    : contentSelection.handleContainerMouseDown
                }
              >
                {!isDisplayedSyntheticNode &&
                  displayedNode &&
                  !(isOpenedFileSelected && isImageNode(displayedNode)) && (
                    <div
                      className={cn(
                        "mb-4 rounded-md border border-border px-3 py-2",
                        isEditingDescription ? "bg-background" : "bg-muted/20"
                      )}
                    >
                      <div
                        className={cn(
                          isEditingDescription
                            ? "space-y-2"
                            : "flex items-start justify-between gap-3"
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex min-h-8 items-center justify-between gap-3">
                            <div className="text-xs font-medium text-foreground">
                              {t("knowledgeBase.treeView.descriptionLabel")}
                            </div>
                            {isEditingDescription && (
                              <div className="flex shrink-0 items-center gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 gap-1"
                                  onClick={saveEditDescription}
                                  disabled={isSavingDescription}
                                  title={t("knowledgeBase.treeView.save")}
                                  aria-label={t("knowledgeBase.treeView.save")}
                                >
                                  <Check className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                                  <span className="text-xs">
                                    {t("knowledgeBase.treeView.save")}
                                  </span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 gap-1"
                                  onClick={cancelEditDescription}
                                  disabled={isSavingDescription}
                                  title={t("knowledgeBase.treeView.cancel")}
                                  aria-label={t(
                                    "knowledgeBase.treeView.cancel"
                                  )}
                                >
                                  <X className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                                  <span className="text-xs">
                                    {t("knowledgeBase.treeView.cancel")}
                                  </span>
                                </Button>
                              </div>
                            )}
                          </div>
                          {isEditingDescription ? (
                            <textarea
                              className="min-h-20 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              value={editDescription}
                              onChange={(e) =>
                                setEditDescription(e.target.value)
                              }
                              placeholder={t(
                                "knowledgeBase.treeView.descriptionPlaceholder"
                              )}
                              aria-label={t(
                                "knowledgeBase.treeView.descriptionLabel"
                              )}
                            />
                          ) : (
                            <p
                              className={cn(
                                "mt-1 truncate text-sm",
                                displayedNode.description?.trim()
                                  ? "text-muted-foreground"
                                  : "text-muted-foreground/70"
                              )}
                              title={displayedNode.description || undefined}
                            >
                              {displayedNode.description?.trim() ||
                                t("knowledgeBase.treeView.descriptionEmpty")}
                            </p>
                          )}
                        </div>
                        {isEditingDescription ? null : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 shrink-0 p-0"
                            onClick={startEditDescription}
                            title={t(
                              displayedNode.description?.trim()
                                ? "knowledgeBase.treeView.editDescription"
                                : "knowledgeBase.treeView.addDescription"
                            )}
                            aria-label={t(
                              displayedNode.description?.trim()
                                ? "knowledgeBase.treeView.editDescription"
                                : "knowledgeBase.treeView.addDescription"
                            )}
                          >
                            <Pencil className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                {isOpenedFileSelected &&
                  selectedNode.node_type === "FILE" &&
                  (selectedIsImage ? (
                    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
                      <section
                        className="relative min-h-80 overflow-hidden rounded-lg border border-border bg-slate-50 dark:bg-slate-950"
                        aria-label={t(
                          "knowledgeBase.treeView.imagePreviewLabel"
                        )}
                      >
                        <KnowledgeBaseImagePreview
                          key={selectedNode.id}
                          src={selectedImageMediaUrl}
                          alt={selectedNode.name}
                          pending={selectedImageIsPending}
                          className="h-[min(60vh,42rem)] min-h-80 w-full"
                          loadingLabel={t(
                            "knowledgeBase.treeView.imagePreviewLoading"
                          )}
                          errorLabel={t(
                            "knowledgeBase.treeView.imagePreviewUnavailable"
                          )}
                        />
                        {(selectedNode.status === "PENDING" ||
                          selectedNode.status === "INDEXING") && (
                          <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-md border border-border bg-background/90 px-2 py-1 text-xs font-medium text-foreground shadow-sm backdrop-blur-sm">
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
                            {t(
                              "knowledgeBase.treeView.content.statuses.indexing"
                            )}
                          </div>
                        )}
                      </section>

                      <aside className="overflow-hidden rounded-lg border border-border bg-background">
                        <section className="p-4">
                          <h4 className="text-sm font-semibold text-foreground">
                            {t("knowledgeBase.treeView.fileInformation")}
                          </h4>
                          <dl className="mt-3 space-y-2.5 text-sm">
                            <div className="flex items-center justify-between gap-4">
                              <dt className="text-muted-foreground">
                                {t("knowledgeBase.treeView.fileType")}
                              </dt>
                              <dd className="font-medium text-foreground">
                                {t("knowledgeBase.treeView.imageType")}
                              </dd>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <dt className="text-muted-foreground">
                                {t("knowledgeBase.treeView.fileFormat")}
                              </dt>
                              <dd className="font-medium text-foreground">
                                {getImageFormatLabel(
                                  selectedNode.content_type
                                ) || "—"}
                              </dd>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <dt className="text-muted-foreground">
                                {t("knowledgeBase.treeView.fileSize")}
                              </dt>
                              <dd className="font-medium text-foreground">
                                {selectedNode.size_bytes != null
                                  ? formatBytes(selectedNode.size_bytes)
                                  : "—"}
                              </dd>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                              <dt className="text-muted-foreground">
                                {t("knowledgeBase.treeView.fileStatus")}
                              </dt>
                              <dd
                                className={cn(
                                  "inline-flex items-center gap-1.5 font-medium",
                                  selectedNode.status === "FAILED"
                                    ? "text-red-600 dark:text-red-400"
                                    : "text-foreground"
                                )}
                              >
                                <span
                                  className={cn(
                                    "h-1.5 w-1.5 rounded-full",
                                    selectedNode.status === "FAILED"
                                      ? "bg-red-500"
                                      : selectedNode.status === "READY"
                                        ? "bg-emerald-500"
                                        : "bg-blue-500"
                                  )}
                                />
                                {t(
                                  `knowledgeBase.treeView.content.statuses.${getNodeStatusKey(
                                    selectedNode
                                  )}`
                                )}
                              </dd>
                            </div>
                          </dl>
                          {selectedNode.status === "FAILED" && (
                            <p className="mt-3 text-sm leading-5 text-red-600 dark:text-red-400">
                              {t("knowledgeBase.treeView.imageIndexingFailed")}
                            </p>
                          )}
                        </section>

                        <section className="border-t border-border p-4">
                          <h4 className="text-sm font-semibold text-foreground">
                            {t("knowledgeBase.treeView.aiSearchDescription")}
                          </h4>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            {t(
                              "knowledgeBase.treeView.aiSearchDescriptionHint"
                            )}
                          </p>
                          <div className="mt-3">
                            {selectedNode.status !== "READY" ? (
                              <p className="text-sm leading-6 text-muted-foreground">
                                {t(
                                  "knowledgeBase.treeView.imageDescriptionPending"
                                )}
                              </p>
                            ) : !selectedNode.content_loaded ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t(
                                  "knowledgeBase.treeView.imageDescriptionLoading"
                                )}
                              </div>
                            ) : selectedImageDescription ? (
                              <>
                                <p
                                  className={cn(
                                    "whitespace-pre-wrap text-sm leading-6 text-foreground",
                                    !isImageDescriptionExpanded &&
                                      "line-clamp-4"
                                  )}
                                >
                                  {selectedImageDescription}
                                </p>
                                {canExpandSelectedImageDescription && (
                                  <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="mt-2 h-auto p-0 text-sm"
                                    aria-expanded={isImageDescriptionExpanded}
                                    onClick={() =>
                                      setIsImageDescriptionExpanded(
                                        (value) => !value
                                      )
                                    }
                                  >
                                    {t(
                                      isImageDescriptionExpanded
                                        ? "knowledgeBase.treeView.showLessDescription"
                                        : "knowledgeBase.treeView.showFullDescription"
                                    )}
                                  </Button>
                                )}
                              </>
                            ) : (
                              <p className="text-sm leading-6 text-muted-foreground">
                                {t(
                                  "knowledgeBase.treeView.imageDescriptionUnavailable"
                                )}
                              </p>
                            )}
                          </div>
                        </section>
                      </aside>
                    </div>
                  ) : !selectedPotentialTabular ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        {(selectedNode.status === "PENDING" ||
                          selectedNode.status === "INDEXING") && (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                            <span className="text-slate-600 dark:text-slate-300">
                              {t(
                                "knowledgeBase.treeView.content.statuses.indexing"
                              )}
                            </span>
                          </>
                        )}
                        {selectedNode.status === "FAILED" && (
                          <span className="text-red-600 dark:text-red-400">
                            {t(
                              "knowledgeBase.treeView.content.statuses.failed"
                            )}
                          </span>
                        )}
                      </div>
                      {selectedNode.status === "FAILED" && (
                        <div className="text-sm text-red-600 dark:text-red-400">
                          {t("knowledgeBase.treeView.imageIndexingFailed")}
                        </div>
                      )}

                      {selectedNode.status === "READY" && (
                        <div className="prose prose-sm dark:prose-invert max-w-none bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                          {!selectedNode.content_loaded ? (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t("knowledgeBase.treeView.contentLoading")}
                            </div>
                          ) : selectedPreviewContent !== null &&
                            selectedPreviewContent !== "" &&
                            currentOrganization?.id &&
                            selectedNode.workspace_id ? (
                            <KnowledgeBaseTextRangeFaqViewer
                              key={selectedNode.id}
                              content={selectedPreviewContent}
                              organizationId={currentOrganization.id}
                              workspaceId={selectedNode.workspace_id}
                              kbId={selectedNode.knowledge_base_id}
                              nodeId={selectedNode.id}
                            />
                          ) : (
                            <div className="text-sm text-slate-500">
                              {t("knowledgeBase.treeView.noPreviewAvailable")}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null)}

                {isOpenedFileSelected &&
                  selectedNode.node_type === "FILE" &&
                  selectedPotentialTabular && (
                    <div className="space-y-3">
                      {selectedTabularResolutionPending ? (
                        <div className="flex items-center gap-2 rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
                          <Loader2 className="size-4 animate-spin" />
                          {t("knowledgeBase.treeView.tabular.loading")}
                        </div>
                      ) : selectedTabularResolutionError ? (
                        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
                          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                          {t(
                            "knowledgeBase.treeView.tabular.loadCapabilitiesFailed"
                          )}
                        </div>
                      ) : selectedTabularFeatureEnabled &&
                        currentOrganization?.id &&
                        selectedNode.workspace_id ? (
                        <TabularFileDetail
                          node={selectedNode}
                          organizationId={currentOrganization.id}
                          workspaceId={selectedNode.workspace_id}
                          canEdit={canEditSelectedWorkspace}
                          onLifecycleChange={() =>
                            selectedNode.parent_id
                              ? kb.refreshFolderChildren(selectedNode.parent_id)
                              : undefined
                          }
                        />
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-sm">
                            {(selectedNode.status === "PENDING" ||
                              selectedNode.status === "INDEXING") && (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                                <span className="text-slate-600 dark:text-slate-300">
                                  {t(
                                    "knowledgeBase.treeView.content.statuses.indexing"
                                  )}
                                </span>
                              </>
                            )}
                            {selectedNode.status === "FAILED" && (
                              <span className="text-red-600 dark:text-red-400">
                                {t(
                                  "knowledgeBase.treeView.content.statuses.failed"
                                )}
                              </span>
                            )}
                          </div>
                          {selectedNode.status === "FAILED" && (
                            <div className="text-sm text-red-600 dark:text-red-400">
                              {t("knowledgeBase.treeView.indexingFailed")}
                            </div>
                          )}

                          {selectedNode.status === "READY" && (
                            <div className="prose prose-sm dark:prose-invert max-w-none bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                              {!selectedNode.content_loaded ? (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  {t("knowledgeBase.treeView.loadingContent")}
                                </div>
                              ) : selectedPreviewContent !== null &&
                                selectedPreviewContent !== "" &&
                                currentOrganization?.id &&
                                selectedNode.workspace_id ? (
                                <KnowledgeBaseTextRangeFaqViewer
                                  key={selectedNode.id}
                                  content={selectedPreviewContent}
                                  organizationId={currentOrganization.id}
                                  workspaceId={selectedNode.workspace_id}
                                  kbId={selectedNode.knowledge_base_id}
                                  nodeId={selectedNode.id}
                                />
                              ) : (
                                <div className="text-sm text-slate-500">
                                  {t(
                                    "knowledgeBase.treeView.noPreviewAvailable"
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                {/* Folder contents view (always shown; selection does not navigate) */}
                {!isOpenedFileSelected && (
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-foreground">
                      {t("knowledgeBase.treeView.contents")}
                    </h4>
                    {!currentFolderNode.children_loaded ? (
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                        {[0, 1, 2].map((idx) => (
                          <div
                            key={idx}
                            className="flex min-h-[68px] items-center gap-3 rounded-md border border-border bg-background px-3 py-3"
                          >
                            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                              <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : currentFolderContentItems.length === 0 ? (
                      <div className="rounded-md border border-dashed border-border bg-muted/20 px-6 py-8 text-center">
                        <div className="text-sm font-medium text-foreground">
                          {t("knowledgeBase.treeView.content.emptyTitle")}
                        </div>
                        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                          {t("knowledgeBase.treeView.content.emptyDescription")}
                        </p>
                      </div>
                    ) : (
                      <div className="relative">
                        {contentSelection.marqueeRect && (
                          <div
                            className="pointer-events-none fixed z-10 border border-blue-400/80 bg-blue-200/20"
                            style={{
                              left: contentSelection.marqueeRect.left,
                              top: contentSelection.marqueeRect.top,
                              width: contentSelection.marqueeRect.width,
                              height: contentSelection.marqueeRect.height,
                            }}
                          />
                        )}
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
                          {currentFolderContentItems.map((item) => {
                            const child = item
                            const isSelectedChild =
                              contentSelection.selectedIds.has(child.id)
                            const statusKey = getNodeStatusKey(child)
                            const childIsImage = isImageNode(child)
                            const childImageIsPending =
                              child.status === "PENDING" ||
                              child.status === "INDEXING"
                            const childImageMediaUrl =
                              childIsImage && child.status === "READY"
                                ? kb.getFileMediaUrl(child)
                                : null
                            const childMeta =
                              child.node_type === "FOLDER"
                                ? [
                                    child.ui_kind === "WORKSPACE"
                                      ? t(
                                          "knowledgeBase.treeView.workspaceType"
                                        )
                                      : child.source_type === "WEBSITE_CRAWLER"
                                        ? t(
                                            "knowledgeBase.treeView.crawlWebsite"
                                          )
                                        : t(
                                            "knowledgeBase.treeView.folderType"
                                          ),
                                    child.children_loaded
                                      ? `${child.children?.length || 0} ${t("knowledgeBase.treeView.items")}`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")
                                : childIsImage
                                  ? [
                                      t("knowledgeBase.treeView.imageType"),
                                      getImageFormatLabel(child.content_type),
                                      child.size_bytes != null
                                        ? formatBytes(child.size_bytes)
                                        : null,
                                      statusKey === "ready"
                                        ? null
                                        : t(
                                            `knowledgeBase.treeView.content.statuses.${statusKey}`
                                          ),
                                    ]
                                      .filter(Boolean)
                                      .join(" · ")
                                  : statusKey === "ready"
                                    ? t("knowledgeBase.treeView.documentType")
                                    : `${t("knowledgeBase.treeView.documentType")} · ${t(
                                        `knowledgeBase.treeView.content.statuses.${statusKey}`
                                      )}`
                            return (
                              <div
                                key={child.id}
                                className={cn(
                                  "group relative rounded-md border border-border bg-background text-sm transition-colors",
                                  isSelectedChild
                                    ? "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
                                    : "hover:bg-muted/40"
                                )}
                              >
                                <button
                                  type="button"
                                  data-kb-content-item="true"
                                  ref={(el) =>
                                    contentSelection.registerItemElement(
                                      child.id,
                                      el
                                    )
                                  }
                                  onClick={(e) => selectFromContents(child, e)}
                                  onDoubleClick={() => {
                                    void openFromContents(child)
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== "Enter") return
                                    e.preventDefault()
                                    void openFromContents(child)
                                  }}
                                  aria-pressed={isSelectedChild}
                                  className={cn(
                                    "flex w-full cursor-default items-center gap-3 rounded-md px-3 py-3 pr-12 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                                    childIsImage ? "min-h-28" : "min-h-[68px]"
                                  )}
                                >
                                  <span className="shrink-0">
                                    {childIsImage ? (
                                      <KnowledgeBaseImagePreview
                                        src={childImageMediaUrl}
                                        alt={child.name}
                                        compact
                                        pending={childImageIsPending}
                                        className="h-20 w-20 rounded-md border border-border"
                                        loadingLabel={t(
                                          "knowledgeBase.treeView.imagePreviewLoading"
                                        )}
                                        errorLabel={t(
                                          "knowledgeBase.treeView.imagePreviewUnavailable"
                                        )}
                                      />
                                    ) : (
                                      renderNodeIcon(child, {
                                        isExpanded: false,
                                        isWebsiteCrawlSyncing:
                                          isWebsiteCrawlerFolderSyncing(child),
                                      })
                                    )}
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span
                                      className={cn(
                                        "block truncate font-medium",
                                        isSelectedChild
                                          ? "text-blue-700 dark:text-blue-200"
                                          : "text-foreground"
                                      )}
                                      title={child.name}
                                    >
                                      {child.name}
                                    </span>
                                    <span
                                      className={cn(
                                        "mt-0.5 block truncate text-xs",
                                        isSelectedChild
                                          ? "text-blue-600/80 dark:text-blue-200/80"
                                          : "text-muted-foreground"
                                      )}
                                    >
                                      {childMeta}
                                    </span>
                                  </span>
                                </button>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className={cn(
                                        "absolute right-3 top-1/2 h-7 w-7 -translate-y-1/2 p-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
                                        isSelectedChild && "opacity-100"
                                      )}
                                      aria-label={t(
                                        "knowledgeBase.treeView.open"
                                      )}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        void openFromContents(child)
                                      }}
                                    >
                                      <ArrowRight className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    {t("knowledgeBase.treeView.open")}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <FileText className="w-16 h-16 mb-4 opacity-30" />
            <p className="text-sm">{t("knowledgeBase.treeView.selectItem")}</p>
          </div>
        )}
      </div>

      <Dialog
        open={isUploadDialogOpen}
        onOpenChange={(open) => {
          setIsUploadDialogOpen(open)
          if (!open) setFileUploadTargetFolderId(null)
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t("knowledgeBase.treeView.tabular.uploadTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("knowledgeBase.treeView.tabular.uploadDescription", {
                formats:
                  uploadTabularCapabilities?.accepted_extensions.join(", ") ??
                  ".csv, .xlsx",
                size: formatBytes(
                  uploadTabularCapabilities?.max_source_bytes ?? 0
                ),
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            <p>{t("knowledgeBase.treeView.tabular.visibleSheetsOnly")}</p>
            <p>{t("knowledgeBase.treeView.tabular.cachedFormulaValues")}</p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsUploadDialogOpen(false)}
            >
              {t("knowledgeBase.treeView.cancel")}
            </Button>
            <Button type="button" onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-2 size-4" />
              {t("knowledgeBase.treeView.tabular.chooseFiles")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden file input for uploading file */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileSelect}
        multiple
        accept={
          uploadTabularCapabilities?.enabled
            ? [
                KB_FILE_ACCEPT_STRING,
                ...uploadTabularCapabilities.accepted_extensions,
                ...uploadTabularCapabilities.accepted_mime_types,
              ].join(",")
            : KB_FILE_ACCEPT_STRING
        }
      />

      {/* New Folder Dialog */}
      <Dialog
        open={isNewFolderDialogOpen}
        onOpenChange={setIsNewFolderDialogOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {t("knowledgeBase.treeView.newFolderTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-1">
              <Label className="text-xs text-slate-500 dark:text-slate-400">
                {t("knowledgeBase.treeView.newFolderLocation")}
              </Label>
              <div
                className="text-sm text-slate-700 dark:text-slate-200 truncate"
                title={targetFolderPath || ""}
              >
                {targetFolderPath || "-"}
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="folder-name">
                {t("knowledgeBase.treeView.folderName")}
              </Label>
              <Input
                id="folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder={t("knowledgeBase.treeView.folderNamePlaceholder")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderName.trim()) {
                    handleConfirmNewFolder()
                  }
                }}
                autoFocus
                className={
                  hasDuplicateNameInCreate
                    ? "border-red-500 focus-visible:ring-red-500"
                    : undefined
                }
              />
              {hasDuplicateNameInCreate && (
                <div className="text-xs text-red-500">
                  {t("knowledgeBase.treeView.duplicateNameWarning")}
                </div>
              )}
            </div>
            <div className="grid gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <Label htmlFor="folder-description">
                  {t("knowledgeBase.treeView.newFolderFolderDescription")}
                </Label>
                <span className="text-xs text-slate-400">
                  {t("knowledgeBase.treeView.newFolderFolderDescriptionHelp")}
                </span>
              </div>
              <textarea
                id="folder-description"
                className="w-full min-h-[88px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 text-sm text-slate-700 dark:text-slate-300"
                value={newFolderDescription}
                onChange={(e) => setNewFolderDescription(e.target.value)}
                placeholder={t(
                  "knowledgeBase.treeView.newFolderFolderDescriptionPlaceholder"
                )}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsNewFolderDialogOpen(false)}
            >
              {t("knowledgeBase.treeView.cancel")}
            </Button>
            <Button
              onClick={handleConfirmNewFolder}
              disabled={
                !newFolderName.trim() ||
                isCreatingFolder ||
                hasDuplicateNameInCreate
              }
            >
              {t("knowledgeBase.treeView.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WebsiteCrawlerDialog
        mode="create"
        open={websiteCrawlerDialog.isCreateDialogOpen}
        onOpenChange={websiteCrawlerDialog.setIsCreateDialogOpen}
        form={websiteCrawlerDialog.createForm}
        onFormChange={websiteCrawlerDialog.updateCreateForm}
        isSubmitting={websiteCrawlerDialog.isCreatingWebsiteCrawl}
        canSubmit={websiteCrawlerDialog.canCreateWebsiteCrawl}
        canSync={false}
        isSaving={websiteCrawlerDialog.isCreatingWebsiteCrawl}
        websiteCrawlCreditRatePerPage={
          websiteCrawlerDialog.websiteCrawlCreditRatePerPage
        }
        availableCredits={websiteCrawlerDialog.availableCredits}
        onLoadBillingOverview={websiteCrawlerDialog.refreshBillingOverview}
        onSave={() => {
          void handleConfirmCreateWebsiteCrawl()
        }}
        onSync={() => undefined}
      />

      <WebsiteCrawlerDialog
        mode="manage"
        folderName={currentFolderNode.name}
        open={websiteCrawlerDialog.isOpen}
        onOpenChange={websiteCrawlerDialog.setIsOpen}
        form={websiteCrawlerDialog.form}
        onFormChange={websiteCrawlerDialog.updateForm}
        target={websiteCrawlerDialog.target}
        job={websiteCrawlerDialog.job}
        isJobActive={websiteCrawlerDialog.isJobActive}
        isDirty={websiteCrawlerDialog.isDirty}
        canSave={websiteCrawlerDialog.canSave}
        canSync={websiteCrawlerDialog.canSync}
        isSaving={websiteCrawlerDialog.isSaving}
        isSyncing={websiteCrawlerDialog.isSyncing}
        isCanceling={websiteCrawlerDialog.isCanceling}
        failureDetails={websiteCrawlerDialog.failureDetails}
        failureDetailsTotal={websiteCrawlerDialog.failureDetailsTotal}
        failureDetailsStatus={websiteCrawlerDialog.failureDetailsStatus}
        isFailureDetailsOpen={websiteCrawlerDialog.isFailureDetailsOpen}
        isLoadingFailureDetails={websiteCrawlerDialog.isLoadingFailureDetails}
        websiteCrawlCreditRatePerPage={
          websiteCrawlerDialog.websiteCrawlCreditRatePerPage
        }
        availableCredits={websiteCrawlerDialog.availableCredits}
        onLoadBillingOverview={websiteCrawlerDialog.refreshBillingOverview}
        onShowFailureDetails={websiteCrawlerDialog.showFailureDetails}
        onFailureDetailsOpenChange={websiteCrawlerDialog.setFailureDetailsOpen}
        onSave={() => {
          void websiteCrawlerDialog.save()
        }}
        onSync={() => {
          void websiteCrawlerDialog.sync()
        }}
        onCancel={() => {
          void websiteCrawlerDialog.cancel()
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>
              {nodesToDelete.length > 1
                ? t("knowledgeBase.treeView.deleteMultipleTitle")
                : t("knowledgeBase.treeView.deleteTitle")}
            </DialogTitle>
            <DialogDescription className="text-wrap break-all">
              {nodesToDelete.length > 1
                ? t("knowledgeBase.treeView.deleteMultipleWarning", {
                    count: nodesToDelete.length,
                  })
                : nodesToDelete[0]?.node_type === "FOLDER"
                  ? t("knowledgeBase.treeView.deleteFolderWarning", {
                      name: nodesToDelete[0]?.name,
                    })
                  : t("knowledgeBase.treeView.deleteDocumentWarning", {
                      name: nodesToDelete[0]?.name,
                    })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false)
                setNodesToDelete([])
              }}
            >
              {t("knowledgeBase.treeView.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeletingNode}
            >
              {t("knowledgeBase.treeView.confirmDelete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/**
 * Individual tree node component
 */
interface TreeNodeProps {
  // Synthetic root node id (used to hide actions and treat root as UI-only).
  rootId: string
  // Node being rendered (includes UI-only workspace nodes and real KB nodes).
  node: KnowledgeNode
  // Nesting depth (used for left padding indentation).
  depth: number
  // Global expansion state owned by TreeView.
  expandedNodes: TreeNodeState
  // Currently selected node (used for row highlight).
  selectedNode: KnowledgeNode | null
  // Expand/collapse a folder node.
  onToggleExpand: (nodeId: string, e?: React.MouseEvent) => void
  // Select a node (also triggers lazy-load for folders via TreeView handler).
  onSelect: (node: KnowledgeNode) => void
  // Open the "New folder" dialog for a folder id.
  onNewFolder: (folderId: string) => void
  // Open the native file picker for local upload into a folder id.
  onUpload: (folderId: string) => void
  // Connections available in the workspace (used to show provider actions).
  connections: Array<{
    id: string
    name: string
    status: string
    provider: string
  }>
  isStorageAtLimit: boolean
  // Add files from an existing Google Drive connection.
  onAddFromGoogleDriveConnection: (
    folderId: string,
    connectionId: string
  ) => void
  // Add files from an existing Notion connection.
  onAddFromNotionConnection: (folderId: string, connectionId: string) => void
  // Start OAuth flow to connect another Google account, then open picker.
  onConnectAnotherGoogleAccount: (folderId: string) => void
  // Start OAuth flow to connect another Notion workspace, then import all authorized pages.
  onConnectAnotherNotionWorkspace: (folderId: string) => void
  // Delete a connection.
  onDeleteConnection: (connectionId: string) => void
  // Open the delete confirmation dialog for a node.
  onDelete: (node: KnowledgeNode) => void
  // Open website sync settings dialog for a crawler folder.
  onOpenWebsiteSyncSettings: (node: KnowledgeNode) => void
  isWebsiteCrawlerFolderSyncing: (node: KnowledgeNode) => boolean
  // Lazy-load children for a folder (used before expanding). Return value is ignored by TreeNode.
  onEnsureChildrenLoaded: (folderId: string) => Promise<KnowledgeNode[] | null>
  // Download uploaded originals or external text snapshots.
  onDownload: (node: KnowledgeNode) => void
  // Drag-and-drop handlers resolved by parent hook for upload targets.
  dragHandlers: (node: KnowledgeNode) => {
    onDragEnter: (e: React.DragEvent<HTMLElement>) => void
    onDragOver: (e: React.DragEvent<HTMLElement>) => void
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void
    onDrop: (e: React.DragEvent<HTMLElement>) => void
  }
  activeDropTargetNodeId: string | null
}

const TreeNode = ({
  rootId,
  node,
  depth,
  expandedNodes,
  selectedNode,
  onToggleExpand,
  onSelect,
  onNewFolder,
  onUpload,
  connections,
  isStorageAtLimit,
  onAddFromGoogleDriveConnection,
  onAddFromNotionConnection,
  onConnectAnotherGoogleAccount,
  onConnectAnotherNotionWorkspace,
  onDeleteConnection,
  onDelete,
  onOpenWebsiteSyncSettings,
  isWebsiteCrawlerFolderSyncing,
  onEnsureChildrenLoaded,
  onDownload,
  dragHandlers,
  activeDropTargetNodeId,
}: TreeNodeProps) => {
  const { t } = useTranslation()
  const isRoot = node.id === rootId
  // Workspace nodes are UI-only; they don't support per-node actions.
  const isWorkspace = node.ui_kind === "WORKSPACE"
  // Folders can have children and support actions like "New folder" and "Upload".
  const isFolder = node.node_type === "FOLDER"
  // Expansion state comes from parent TreeView.
  const isExpanded = expandedNodes[node.id]
  // Selection state comes from parent TreeView.
  const isSelected = selectedNode?.id === node.id
  const isDropTargetContainer = activeDropTargetNodeId === node.id
  const nodeDragHandlers = dragHandlers(node)
  // Render children only if we have them in memory (children are lazy-loaded).
  const hasChildren = isFolder && node.children && node.children.length > 0

  return (
    <div
      className={cn(
        isDropTargetContainer &&
          "relative rounded-md ring-2 ring-blue-400/70 ring-inset bg-blue-50/40 dark:bg-blue-900/20"
      )}
    >
      {/* Node row */}
      <div
        className={cn(
          "flex items-center gap-1 py-1 px-1 rounded-md cursor-pointer group transition-colors",
          isSelected
            ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
            : "hover:bg-slate-100 dark:hover:bg-slate-800"
        )}
        data-kb-tree-node-id={node.id}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        // Selecting a node drives right-panel content and detail view.
        onClick={() => onSelect(node)}
        onDragEnter={nodeDragHandlers.onDragEnter}
        onDragOver={nodeDragHandlers.onDragOver}
        onDragLeave={nodeDragHandlers.onDragLeave}
        onDrop={nodeDragHandlers.onDrop}
        onDoubleClick={async (e) => {
          if (!isFolder) return
          e.stopPropagation()
          // If expanding a folder that hasn't loaded its children yet, fetch children first.
          if (!isExpanded) {
            await onEnsureChildrenLoaded(node.id)
          }
          onToggleExpand(node.id, e)
        }}
      >
        {/* Expand/collapse button */}
        <button
          onClick={async (e) => {
            if (!isFolder) return
            e.stopPropagation()
            // If expanding a folder that hasn't loaded its children yet, fetch children first.
            if (!isExpanded) {
              await onEnsureChildrenLoaded(node.id)
            }
            onToggleExpand(node.id, e)
          }}
          className={cn(
            "p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700",
            !isFolder && "invisible"
          )}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
        </button>

        {/* Icon */}
        {renderNodeIcon(node, {
          isExpanded,
          isWebsiteCrawlSyncing: isWebsiteCrawlerFolderSyncing(node),
        })}

        {/* Name */}
        <span
          className={cn(
            "text-sm truncate flex-1",
            isSelected ? "font-medium" : "text-slate-700 dark:text-slate-300"
          )}
          title={node.name}
        >
          {node.name}
        </span>

        {!isRoot && (
          <>
            {/* Item count badge */}
            {isFolder && hasChildren && (
              <span className="text-xs text-slate-400 tabular-nums">
                {node.children?.length}
              </span>
            )}

            {/* Actions dropdown (workspace has limited actions; folders/files have their own actions) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="w-4 h-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-60">
                {isWorkspace ? (
                  // Workspace nodes: only allow creating the single allowed folder layer (KB root) under the workspace.
                  <DropdownMenuItem
                    className="gap-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      onNewFolder(node.id)
                    }}
                  >
                    <FolderPlus className="w-4 h-4" />
                    {t("knowledgeBase.treeView.newFolder")}
                  </DropdownMenuItem>
                ) : isFolder ? (
                  // Folder nodes under a workspace are treated as KB roots; we disallow creating subfolders here.
                  <>
                    <AddFileMenuItems
                      folderId={node.id}
                      connections={connections}
                      isStorageAtLimit={isStorageAtLimit}
                      onUpload={onUpload}
                      onAddFromGoogleDriveConnection={
                        onAddFromGoogleDriveConnection
                      }
                      onAddFromNotionConnection={onAddFromNotionConnection}
                      onConnectAnotherGoogleAccount={
                        onConnectAnotherGoogleAccount
                      }
                      onConnectAnotherNotionWorkspace={
                        onConnectAnotherNotionWorkspace
                      }
                      onDeleteConnection={onDeleteConnection}
                    />

                    <DropdownMenuSeparator />
                    {node.source_type === "WEBSITE_CRAWLER" && (
                      <>
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={(e) => {
                            e.stopPropagation()
                            onOpenWebsiteSyncSettings(node)
                          }}
                        >
                          <Settings className="w-4 h-4" />
                          {t("knowledgeBase.treeView.websiteCrawlSettings")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}

                    <DropdownMenuItem
                      className="gap-2 text-red-600 dark:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(node)
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      {t("knowledgeBase.treeView.delete")}
                    </DropdownMenuItem>
                  </>
                ) : (
                  // File nodes: download originals or clearly labeled text snapshots.
                  <>
                    <DropdownMenuItem
                      className="gap-2"
                      title={
                        node.source_type !== "LOCAL_UPLOAD"
                          ? t("knowledgeBase.treeView.downloadTextSnapshotHint")
                          : undefined
                      }
                      onClick={(e) => {
                        e.stopPropagation()
                        onDownload(node)
                      }}
                    >
                      <Download className="w-4 h-4" />
                      {t(getKnowledgeBaseDownloadLabelKey([node]))}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="gap-2 text-red-600 dark:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(node)
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      {t("knowledgeBase.treeView.delete")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>

      {/* Children */}
      {isFolder && isExpanded && hasChildren && (
        <div>
          {node.children?.map((child) => (
            <TreeNode
              key={child.id}
              rootId={rootId}
              node={child}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              selectedNode={selectedNode}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
              onNewFolder={onNewFolder}
              onUpload={onUpload}
              connections={connections}
              isStorageAtLimit={isStorageAtLimit}
              onAddFromGoogleDriveConnection={onAddFromGoogleDriveConnection}
              onAddFromNotionConnection={onAddFromNotionConnection}
              onConnectAnotherGoogleAccount={onConnectAnotherGoogleAccount}
              onConnectAnotherNotionWorkspace={onConnectAnotherNotionWorkspace}
              onDeleteConnection={onDeleteConnection}
              onDelete={onDelete}
              onOpenWebsiteSyncSettings={onOpenWebsiteSyncSettings}
              isWebsiteCrawlerFolderSyncing={isWebsiteCrawlerFolderSyncing}
              onEnsureChildrenLoaded={onEnsureChildrenLoaded}
              onDownload={onDownload}
              dragHandlers={dragHandlers}
              activeDropTargetNodeId={activeDropTargetNodeId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default TreeView
